"use client";

import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Button } from "@/components/ui/button";
import { ArrowRightIcon, CheckIcon } from "@radix-ui/react-icons";
import { cn } from "@/lib/utils";
import { usePublicAuth } from "@/lib/public-auth";
import { NAV_SIGNED_OUT_VARIANT_CLASS } from "@/lib/nav-auth-hint";
import {
  phCapture,
  phCaptureForNavigation,
  phGetFeatureFlagResult,
  phOnFeatureFlags,
} from "@/utils/analytics";
import { DISCORD_URL, HOME_TRIAL_HREF, TRIAL_TERMS_SHORT } from "@/lib/landing-constants";
import { DiscordMark } from "@/components/ui/discord-mark";
import { TrustSignals } from "@/components/ui/trust-signals";
import { Minus, Quote, TrendingUp } from "lucide-react";
import { PRICING_TESTIMONIALS } from "@/lib/testimonial-data";
import { EdgeStrip } from "@/components/ui/edge-strip";
import dynamic from "next/dynamic";
import type { UpgradeQuoteSource } from "@/components/billing/UpgradeQuoteModal";
import { LazyUpgradeQuoteModal } from "@/components/billing/LazyUpgradeQuoteModal";
import type { PlanChooserCloseReason } from "@/components/ui/plan-chooser";

const loadPlanChooser = () => import("@/components/ui/plan-chooser");
const PlanChooser = dynamic(() => loadPlanChooser().then((mod) => mod.PlanChooser), { ssr: false });
const PHONE_QUERY = "(max-width: 767px)";
const PLAN_CHOOSER_CLOSE_MS = 600;
import {
  appendPricingExperimentQueryParams,
  buildPricingExperimentFeatureFlagProperties,
  DIAMOND_SNIPER_MONTHLY_PRICING_EXPERIMENT_ID,
  PRICING_EXPERIMENT_EXPOSURE_SOURCE,
  resolveEvMonthlyPricingExperiment,
} from "@/lib/pricing-experiment";
import { canOpenUpgradeFlowClient } from "@/lib/billing/upgrade-flow-config";
import { DIAMOND_PRODUCTION_PRICE_IDS_BY_PERIOD } from "@/lib/billing/tier-price-ids";
import { featureGateRoute, normalizeProductTypeParam } from "@/lib/billing/feature-gate";
import { sanitizeRelativeRedirectTarget } from "@/lib/auth/post-login-redirect";
import { getCanonicalSubscriptionSlugForPriceId } from "@/utils/stripe/subscription-slugs";
import { getBundleEntitlements, type BundleTierId as BillingBundleTierId } from "@/lib/billing/bundle-entitlements";
import {
  memberHoldsPlan,
  planChangeLabel,
  planSubscriptionFromRow,
  routePurchase,
  type PlanChangeRoute,
  type PurchaseRoute,
} from "@/lib/billing/purchase-route";


export type ProductType = "ev-plan" | "vip-discord";
export type BundleTierId = BillingBundleTierId;
export type PricingTierId = BundleTierId | "vip-discord";
export type PricingTierKind = "bundle" | "addon";

interface Feature {
  name: string;
  included: boolean;
}

export interface PricingTier {
  id: PricingTierId;
  kind: PricingTierKind;
  name: string;
  price: number;
  billingPeriod: "monthly" | "yearly";
  priceId: string;
  productType: ProductType;
  description: string;
  includedSummary?: string;
  chooserSummary?: string;
  excludedNote?: string;
  savingsNote?: string;
  standaloneNote?: string;
  ctaLabel: string;
  features: Feature[];
  highlight?: boolean;
  badge?: string;
  isNew?: boolean;
  trial: boolean;
  icon: React.ReactNode;
  enabled: boolean;
}

export interface ComparisonTool {
  name: string;
  description: string;
  includedIn: BundleTierId[];
  isNew?: boolean;
}

interface PricingSectionProps {
  tiers: PricingTier[];
  comparison?: ComparisonTool[];
  className?: string;
  showAddOns?: boolean;
}

type UpgradeModalState = {
  targetTier: BundleTierId;
  targetInterval: PricingTier["billingPeriod"];
  source: UpgradeQuoteSource;
} | null;

function formatPrice(price: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: price % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(price);
}

function isDiamondMonthlyTier(tier: PricingTier): boolean {
  return tier.id === "diamond" && tier.kind === "bundle" && tier.billingPeriod === "monthly";
}


const SEAT: Record<
  BundleTierId,
  {
    name: string;
    chip: string;
    rule: string;
    check: string;
    cta: string;
  }
> = {
  silver: {
    name: "font-display font-bold uppercase tracking-tight text-2xl leading-none text-zinc-200",
    chip: "bg-zinc-400/10 text-zinc-300 ring-1 ring-zinc-300/15",
    rule: "bg-gradient-to-r from-zinc-300/50 via-zinc-400/20 to-transparent",
    check: "text-zinc-400",
    cta: "bg-transparent border border-border text-foreground hover:border-zinc-300/40 hover:bg-white/[0.04]",
  },
  gold: {
    name: "font-display font-bold uppercase tracking-tight text-2xl leading-none text-gold",
    chip: "bg-gold/12 text-gold ring-1 ring-gold/30",
    rule: "bg-gradient-to-r from-gold-light/60 via-gold/25 to-transparent",
    check: "text-gold/80",
    cta: "bg-gold/15 border border-gold/45 text-gold hover:bg-gold/25",
  },
  diamond: {
    name: "font-display font-bold uppercase tracking-tight text-2xl leading-none text-primary",
    chip: "bg-primary/20 text-primary ring-1 ring-primary/40",
    rule: "bg-gradient-to-r from-primary/60 via-primary/25 to-transparent",
    check: "text-primary",
    cta: "bg-accent-value text-zinc-950 hover:bg-accent-value-strong",
  },
};

function SearchParamsBridge({
  onParams,
}: {
  onParams: (params: URLSearchParams) => void;
}) {
  const searchParams = useSearchParams();
  useEffect(() => {
    onParams(new URLSearchParams(searchParams?.toString() ?? ""));
  }, [searchParams, onParams]);
  return null;
}

function PricingSection({ tiers, comparison, className, showAddOns = true }: PricingSectionProps) {
  const { subscriptions, isLoading: isAuthLoading, user } = usePublicAuth();
  const pathname = usePathname();
  const router = useRouter();
  const [searchParams, setSearchParams] = useState<URLSearchParams | null>(null);
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    setHydrated(true);
  }, []);
  const priceText = (tier: PricingTier, amount: number) =>
    hydrated || !isDiamondMonthlyTier(tier) ? (
      formatPrice(amount)
    ) : (
      <span
        aria-hidden="true"
        className="inline-block h-[0.72em] w-[3.6ch] animate-pulse rounded bg-current opacity-20 align-baseline"
      />
    );
  const bioPage = Boolean(pathname?.startsWith("/learning-center/bio"));
  const surface = pathname?.startsWith("/pricing")
    ? "pricing_page"
    : bioPage
      ? "creator_bio_pricing_section"
      : "home_pricing_section";
  const chooserSurface = bioPage ? "creator_bio_plan_chooser" : "home_plan_chooser";
  const redirectTo = searchParams?.get("redirect_to")?.trim() || undefined;
  const couponParam = searchParams?.get("coupon")?.trim() || undefined;
  const productTypeParam = searchParams?.get("product_type") || null;
  const requiredProduct = normalizeProductTypeParam(productTypeParam);
  const upgradeFlowEnabled = canOpenUpgradeFlowClient(user);

  const [loadingPriceId, setLoadingPriceId] = useState<string | null>(null);
  const [errorPriceId, setErrorPriceId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<PricingTier["billingPeriod"]>("monthly");
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState<UpgradeModalState>(null);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [chooserLoaded, setChooserLoaded] = useState(false);
  const chooserOpenRef = useRef(false);
  const chooserPushedRef = useRef(false);
  const canChoosePlanRef = useRef(false);
  const featureGateAutoOpenRef = useRef<string | null>(null);

  const priceById = useMemo(() => {
    const map = new Map<string, number>();
    for (const tier of tiers) {
      if (!tier.priceId) continue;
      map.set(tier.priceId, tier.price);
    }
    return map;
  }, [tiers]);

  const diamondMonthlyControlTier = useMemo(
    () =>
      tiers.find((tier) => isDiamondMonthlyTier(tier) && tier.enabled) ??
      tiers.find((tier) => isDiamondMonthlyTier(tier)) ??
      null,
    [tiers],
  );

  const diamondMonthlyExperimentTier = useMemo(
    () =>
      tiers.find(
        (tier) =>
          isDiamondMonthlyTier(tier) &&
          !tier.enabled &&
          tier.priceId.trim().length > 0 &&
          tier.priceId !== diamondMonthlyControlTier?.priceId,
      ) ?? null,
    [diamondMonthlyControlTier?.priceId, tiers],
  );

  const hasActiveSubscription = useMemo(
    () =>
      (subscriptions ?? []).some(
        (sub) =>
          (sub.status === "active" || sub.status === "trialing") &&
          new Date(sub.period_end).getTime() > Date.now(),
      ),
    [subscriptions],
  );

  const shouldSendDiamondSniperFlagExposureRef = useRef(false);
  shouldSendDiamondSniperFlagExposureRef.current =
    !isAuthLoading &&
    !hasActiveSubscription &&
    Boolean(diamondMonthlyControlTier?.priceId) &&
    billingPeriod === "monthly";

  const getFreshDiamondSniperExperimentInputs = () => {
    const result = phGetFeatureFlagResult(DIAMOND_SNIPER_MONTHLY_PRICING_EXPERIMENT_ID, {
      fresh: true,
      send_event: shouldSendDiamondSniperFlagExposureRef.current,
    });
    const rawPayload = result?.payload;
    const payload =
      rawPayload && typeof rawPayload === "object" && !Array.isArray(rawPayload)
        ? (rawPayload as Record<string, unknown>)
        : null;

    return {
      featureFlagValue:
        result?.variant ??
        (typeof result?.enabled === "boolean" ? result.enabled : null),
      featureFlagPayload: payload,
    };
  };

  const resolveDiamondPricingExperiment = (
    inputsOverride?: {
      featureFlagValue: string | boolean | null;
      featureFlagPayload: Record<string, unknown> | null;
    },
  ) => {
    if (!diamondMonthlyControlTier?.priceId) return null;

    const { featureFlagValue, featureFlagPayload } =
      inputsOverride ?? getFreshDiamondSniperExperimentInputs();
    return resolveEvMonthlyPricingExperiment({
      controlPriceId: diamondMonthlyControlTier.priceId,
      controlPrice: diamondMonthlyControlTier.price,
      allowedPriceIds: [
        diamondMonthlyControlTier.priceId,
        diamondMonthlyExperimentTier?.priceId,
      ].filter((priceId): priceId is string => Boolean(priceId)),
      featureFlagValue,
      featureFlagPayload,
      priceById,
      experimentId: DIAMOND_SNIPER_MONTHLY_PRICING_EXPERIMENT_ID,
      payloadPriceIdKey: "diamondPriceId",
      pinScope: `${DIAMOND_SNIPER_MONTHLY_PRICING_EXPERIMENT_ID}:diamond`,
    });
  };

  const [diamondMonthlyExperiment, setDiamondMonthlyExperiment] = useState<ReturnType<
    typeof resolveEvMonthlyPricingExperiment
  > | null>(() =>
    resolveDiamondPricingExperiment({
      featureFlagValue: null,
      featureFlagPayload: null,
    }),
  );

  useEffect(() => {
    if (!diamondMonthlyControlTier?.priceId) {
      setDiamondMonthlyExperiment(null);
      return;
    }

    const resolveExperiment = () => {
      setDiamondMonthlyExperiment(resolveDiamondPricingExperiment());
    };

    resolveExperiment();
    phOnFeatureFlags(resolveExperiment);
  }, [
    diamondMonthlyControlTier?.price,
    diamondMonthlyControlTier?.priceId,
    diamondMonthlyExperimentTier?.priceId,
    hasActiveSubscription,
    isAuthLoading,
    priceById,
  ]);

  function getExperimentForTier(tier: PricingTier): ReturnType<
    typeof resolveEvMonthlyPricingExperiment
  > | null {
    if (hasActiveSubscription) return null;
    if (isDiamondMonthlyTier(tier)) return diamondMonthlyExperiment;
    return null;
  }

  function getDisplayPrice(tier: PricingTier): number {
    return getExperimentForTier(tier)?.displayPrice ?? tier.price;
  }

  function getCheckoutPriceId(tier: PricingTier): string {
    return getExperimentForTier(tier)?.checkoutPriceId ?? tier.priceId;
  }

  function getPricingExperimentMetadata(tier: PricingTier) {
    return getExperimentForTier(tier)?.metadata ?? null;
  }

  const bundleVariants = tiers.filter((t) => t.kind === "bundle" && t.enabled);
  const bundleOrder: BundleTierId[] = [];
  const variantsById = new Map<
    BundleTierId,
    Partial<Record<PricingTier["billingPeriod"], PricingTier>>
  >();
  for (const t of bundleVariants) {
    const id = t.id as BundleTierId;
    if (!variantsById.has(id)) {
      variantsById.set(id, {});
      bundleOrder.push(id);
    }
    variantsById.get(id)![t.billingPeriod] = t;
  }
  const hasMonthly = bundleVariants.some((t) => t.billingPeriod === "monthly");
  const hasYearly = bundleVariants.some((t) => t.billingPeriod === "yearly");
  const bundles = bundleOrder.map((id) => {
    const v = variantsById.get(id)!;
    return v[billingPeriod] ?? v.monthly ?? v.yearly!;
  });
  const yearlySavingsById = new Map<BundleTierId, { pct: number; perMonth: number }>();
  for (const id of bundleOrder) {
    const v = variantsById.get(id)!;
    if (v.monthly && v.yearly && getDisplayPrice(v.monthly) > 0) {
      yearlySavingsById.set(id, {
        pct: Math.round((1 - v.yearly.price / (getDisplayPrice(v.monthly) * 12)) * 100),
        perMonth: v.yearly.price / 12,
      });
    }
  }
  const maxYearlySavingsPct = Math.max(
    0,
    ...Array.from(yearlySavingsById.values(), (s) => s.pct),
  );
  const addonPool = showAddOns ? tiers.filter((t) => t.kind === "addon" && t.enabled) : [];
  const addonOrder: PricingTierId[] = [];
  const addonVariantsById = new Map<
    PricingTierId,
    Partial<Record<PricingTier["billingPeriod"], PricingTier>>
  >();
  for (const t of addonPool) {
    if (!addonVariantsById.has(t.id)) {
      addonVariantsById.set(t.id, {});
      addonOrder.push(t.id);
    }
    addonVariantsById.get(t.id)![t.billingPeriod] = t;
  }
  const addOns = addonOrder.map((id) => {
    const v = addonVariantsById.get(id)!;
    return v[billingPeriod] ?? v.monthly ?? v.yearly!;
  });
  const showComparison = (comparison?.length ?? 0) > 0;
  const planSubscriptions = useMemo(
    () => (subscriptions ?? []).map(planSubscriptionFromRow),
    [subscriptions],
  );

  const pricingExperimentExposureFiredRef = useRef(false);
  const ensurePricingExperimentExposureCaptured = (sendInstantly = false) => {
    if (pricingExperimentExposureFiredRef.current) return;
    if (!shouldSendDiamondSniperFlagExposureRef.current) return;

    if (!diamondMonthlyExperiment?.assignedVariant) return;

    pricingExperimentExposureFiredRef.current = true;
    phCapture(
      PRICING_EXPERIMENT_EXPOSURE_SOURCE,
      {
        surface,
        experiment_id: DIAMOND_SNIPER_MONTHLY_PRICING_EXPERIMENT_ID,
        experiment_variant: diamondMonthlyExperiment.assignedVariant,
        diamond_display_price: diamondMonthlyExperiment.displayPrice,
        diamond_price_id: diamondMonthlyExperiment.checkoutPriceId,
        ...buildPricingExperimentFeatureFlagProperties(diamondMonthlyExperiment.metadata),
      },
      sendInstantly ? { send_instantly: true } : undefined,
    );
  };
  useEffect(() => {
    ensurePricingExperimentExposureCaptured();
  }, [
    billingPeriod,
    diamondMonthlyExperiment,
    hasActiveSubscription,
    isAuthLoading,
    surface,
  ]);

  const openUpgradeModal = (
    targetTier: BundleTierId,
    targetInterval: PricingTier["billingPeriod"],
    source: UpgradeQuoteSource,
  ) => {
    if (!upgradeFlowEnabled) return;
    setUpgradeModal({ targetTier, targetInterval, source });
    setUpgradeModalOpen(true);
  };

  const openUpgradeModalForRoute = (route: PlanChangeRoute, source: UpgradeQuoteSource) => {
    openUpgradeModal(route.targetTier, route.targetInterval === "year" ? "yearly" : "monthly", source);
  };

  const routeFor = (tier: PricingTier): PurchaseRoute =>
    routePurchase(planSubscriptions, {
      priceId: getCheckoutPriceId(tier),
      interval: tier.billingPeriod === "yearly" ? "year" : "month",
    });

  const routeLabel = (route: PurchaseRoute): string | null =>
    route.kind === "plan_change" ? planChangeLabel(route) : route.kind === "fix_billing" ? "Fix billing" : null;

  const firstCtaRef = useRef<HTMLDivElement>(null);
  const comparisonRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      const target = firstCtaRef.current;
      if (!target) return;
      const comparisonRect = comparisonRef.current?.getBoundingClientRect();
      const comparisonInView = Boolean(
        comparisonRect &&
          comparisonRect.top < window.innerHeight - 80 &&
          comparisonRect.bottom > 96,
      );
      setShowStickyCta(target.getBoundingClientRect().bottom < 0 && !comparisonInView);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const hasLiveSubscriptionSlug = (slug: string): boolean =>
    (subscriptions ?? []).some(
      (sub) =>
        getCanonicalSubscriptionSlugForPriceId(sub.price_id) === slug &&
        (sub.status === "active" || sub.status === "trialing") &&
        new Date(sub.period_end).getTime() > Date.now(),
    );

  const hasThisTier = (tier: PricingTier): boolean => {
    if (!tier.priceId || !subscriptions || subscriptions.length === 0) return false;
    const possiblePriceIds = new Set([tier.priceId, getCheckoutPriceId(tier)]);
    if (tier.id === "diamond" && tier.kind === "bundle") {
      for (const id of DIAMOND_PRODUCTION_PRICE_IDS_BY_PERIOD[tier.billingPeriod]) possiblePriceIds.add(id);
    }
    for (const twin of tiers) {
      if (twin.id === tier.id && twin.billingPeriod === tier.billingPeriod && twin.priceId) {
        possiblePriceIds.add(twin.priceId);
      }
    }
    return subscriptions.some(
      (sub) =>
        possiblePriceIds.has(sub.price_id) &&
        (sub.status === "active" || sub.status === "trialing") &&
        new Date(sub.period_end).getTime() > Date.now(),
    );
  };

  const getBundleCtaLabel = (tier: PricingTier): string => routeLabel(routeFor(tier)) ?? tier.ctaLabel;

  useEffect(() => {
    if (!upgradeFlowEnabled || surface !== "pricing_page" || isAuthLoading || !requiredProduct) {
      return;
    }
    const route = featureGateRoute(planSubscriptions, requiredProduct);
    if (route.kind !== "plan_change") return;

    const autoOpenKey = `${productTypeParam ?? requiredProduct}:${route.targetTier}:${route.targetInterval}`;
    if (featureGateAutoOpenRef.current === autoOpenKey) return;

    featureGateAutoOpenRef.current = autoOpenKey;
    openUpgradeModalForRoute(route, "feature_gate");
  }, [
    isAuthLoading,
    planSubscriptions,
    productTypeParam,
    requiredProduct,
    surface,
    upgradeFlowEnabled,
  ]);

  const handleSubscribe = async (tier: PricingTier, placement?: "plan_chooser") => {
    const checkoutPriceId = getCheckoutPriceId(tier);
    const displayPrice = getDisplayPrice(tier);
    const pricingExperimentMetadata = getPricingExperimentMetadata(tier);

    if (isAuthLoading) {
      setErrorPriceId(checkoutPriceId);
      setErrorMessage("Checking your account status. Please try again in a moment.");
      return;
    }

    const route = routeFor(tier);
    if (route.kind === "included") return;
    if (route.kind === "plan_change" && !upgradeFlowEnabled) {
      setErrorPriceId(checkoutPriceId);
      setErrorMessage(
        "You already have access to part of this plan. Contact support to change your plan.",
      );
      return;
    }

    const subscriptionType = `${tier.productType}-${tier.billingPeriod}`;
    const tierProps = {
      surface,
      tier_id: tier.id,
      tier_name: tier.name,
      product_type: tier.productType,
      billing_period: tier.billingPeriod,
      price: displayPrice,
      price_id: checkoutPriceId,
      subscription_type: subscriptionType,
      ...(placement ? { placement } : {}),
      ...(pricingExperimentMetadata ?? {}),
      ...buildPricingExperimentFeatureFlagProperties(pricingExperimentMetadata),
      ...(redirectTo ? { redirect_to: redirectTo } : {}),
    };

    if (route.kind === "fix_billing") {
      await phCaptureForNavigation("pricing_plan_selected", { ...tierProps, checkout_mode: "fix_billing" });
      router.push("/fix-billing");
      return;
    }

    setLoadingPriceId(checkoutPriceId);
    setErrorPriceId(null);
    setErrorMessage(null);

    if (route.kind === "plan_change") {
      await phCaptureForNavigation("pricing_plan_selected", {
        ...tierProps,
        checkout_mode: "upgrade_quote",
      });
      setLoadingPriceId(null);
      openUpgradeModalForRoute(route, "pricing");
      return;
    }

    await phCaptureForNavigation("pricing_plan_selected", {
      ...tierProps,
      checkout_mode: user ? "auth_checkout" : "guest",
    });

    const params = new URLSearchParams({ price_id: checkoutPriceId });
    appendPricingExperimentQueryParams(params, pricingExperimentMetadata);
    if (redirectTo) params.set("redirect_to", redirectTo);
    if (couponParam) params.set("coupon", couponParam);
    router.push(`/checkout?${params.toString()}`);
  };

  const stickyTier = bundles.find((t) => t.highlight) ?? bundles[0];
  const stickyTierDisplayPrice = stickyTier ? getDisplayPrice(stickyTier) : null;
  const stickyTierCheckoutPriceId = stickyTier ? getCheckoutPriceId(stickyTier) : null;
  const stickyTierRoute = stickyTier ? routeFor(stickyTier) : null;
  const stickyTierHasSubscription = stickyTier
    ? hasThisTier(stickyTier) || stickyTierRoute?.kind === "included"
    : false;
  const memberHasPlan = memberHoldsPlan(planSubscriptions);
  const trialCopyClass = isAuthLoading ? NAV_SIGNED_OUT_VARIANT_CLASS : undefined;

  const chooserPage = pathname === "/" || bioPage;
  const chooserTiers = chooserPage
    ? bundleOrder.flatMap((id) => variantsById.get(id)?.monthly ?? [])
    : [];
  canChoosePlanRef.current =
    chooserTiers.length > 0 && chooserTiers.every((tier) => routeFor(tier).kind === "checkout");
  const chooserOptions = chooserTiers.map((tier) => {
    const seat = SEAT[tier.id as BundleTierId] ?? SEAT.silver;
    const checkoutPriceId = getCheckoutPriceId(tier);
    return {
      id: tier.id,
      name: tier.name,
      nameClassName: seat.name,
      price: priceText(tier, getDisplayPrice(tier)),
      summary: tier.chooserSummary ?? tier.description,
      badge: tier.badge,
      badgeClassName: seat.chip,
      label: tier.ctaLabel,
      buttonClassName: seat.cta,
      loading: loadingPriceId === checkoutPriceId,
      error: errorPriceId === checkoutPriceId ? errorMessage : null,
    };
  });

  useEffect(() => {
    if (!chooserPage || typeof window.matchMedia !== "function") return;
    const phone = window.matchMedia(PHONE_QUERY);
    if (phone.matches) void loadPlanChooser();
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const trigger = event.target instanceof Element ? event.target.closest("a[data-plan-chooser]") : null;
      if (!trigger || !canChoosePlanRef.current || !phone.matches) return;
      event.preventDefault();
      if (chooserOpenRef.current) return;
      if (window.location.hash !== HOME_TRIAL_HREF) {
        window.history.pushState(null, "", HOME_TRIAL_HREF);
        chooserPushedRef.current = true;
      }
      chooserOpenRef.current = true;
      setChooserLoaded(true);
      setChooserOpen(true);
      phCapture("plan_chooser_opened", {
        surface: chooserSurface,
        trigger: trigger.getAttribute("data-plan-chooser"),
      });
    };
    const onPopState = () => {
      if (!chooserOpenRef.current) return;
      chooserOpenRef.current = false;
      chooserPushedRef.current = false;
      setChooserOpen(false);
      phCapture("plan_chooser_closed", { surface: chooserSurface, reason: "back" });
    };
    const onScreenWidth = () => {
      if (phone.matches || !chooserOpenRef.current) return;
      chooserOpenRef.current = false;
      setChooserOpen(false);
      phCapture("plan_chooser_closed", { surface: chooserSurface, reason: "dismiss" });
      if (chooserPushedRef.current) {
        chooserPushedRef.current = false;
        window.history.back();
      }
    };
    document.addEventListener("click", onClick);
    window.addEventListener("popstate", onPopState);
    phone.addEventListener("change", onScreenWidth);
    return () => {
      document.removeEventListener("click", onClick);
      window.removeEventListener("popstate", onPopState);
      phone.removeEventListener("change", onScreenWidth);
    };
  }, [chooserPage, chooserSurface]);

  const closeChooser = (reason: PlanChooserCloseReason) => {
    if (!chooserOpenRef.current) return;
    chooserOpenRef.current = false;
    setChooserOpen(false);
    phCapture("plan_chooser_closed", { surface: chooserSurface, reason });
    if (reason === "compare") {
      chooserPushedRef.current = false;
      window.setTimeout(() => document.getElementById("pricing")?.scrollIntoView(), PLAN_CHOOSER_CLOSE_MS);
      return;
    }
    if (chooserPushedRef.current) {
      chooserPushedRef.current = false;
      window.history.back();
    }
  };

  return (
    <section id="pricing" className={cn("relative scroll-mt-24 text-foreground px-4", className)}>
      <Suspense fallback={null}>
        <SearchParamsBridge onParams={setSearchParams} />
      </Suspense>
      <div className="mx-auto w-full max-w-6xl">
        <div className="mb-8 flex flex-col items-start gap-4 text-left">
          {hasMonthly && hasYearly && (
            <div className="flex flex-wrap items-center gap-3">
              <div
                role="group"
                aria-label="Billing period"
                className="inline-flex items-center rounded-md border border-border bg-[var(--color-surface-raised)] p-0.5 data-num text-[11px] font-semibold uppercase tracking-[0.14em]"
              >
                {(["monthly", "yearly"] as const).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setBillingPeriod(p)}
                    aria-pressed={billingPeriod === p}
                    className={cn(
                      "rounded px-3 py-1.5 transition-colors",
                      billingPeriod === p
                        ? "bg-primary text-primary-foreground shadow-[0_0_0_1px_rgba(0,212,255,0.25)]"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {p === "monthly" ? "Monthly" : "Yearly"}
                  </button>
                ))}
              </div>
              {maxYearlySavingsPct > 0 && (
                <span className="inline-flex items-center rounded bg-accent-value/10 px-2 py-1 data-num text-[11px] font-semibold uppercase tracking-wide text-accent-value ring-1 ring-accent-value/25">
                  Save up to{" "}
                  {hydrated ? (
                    `${maxYearlySavingsPct}%`
                  ) : (
                    <span
                      aria-hidden="true"
                      className="inline-block h-[0.9em] w-[3ch] animate-pulse rounded bg-current opacity-20 align-baseline"
                    />
                  )}{" "}
                  yearly
                </span>
              )}
            </div>
          )}
          <span className={cn("data-num text-xs uppercase tracking-[0.14em] text-muted-foreground", billingPeriod === "monthly" && trialCopyClass)}>
            {billingPeriod === "monthly"
              ? memberHasPlan
                ? "Billed monthly · cancel anytime"
                : "5-day trial · $0 charged today · cancel anytime"
              : "Billed annually · no free trial · cancel anytime"}
          </span>
        </div>

        <div className="relative overflow-hidden rounded-[var(--radius-panel)] border border-border bg-[var(--color-surface-raised)]">
          <EdgeStrip />

          <div className="grid grid-cols-1 md:grid-cols-3 md:divide-x md:divide-border md:divide-y-0">
            {bundles.map((tier, index) => {
              const seat = SEAT[tier.id as BundleTierId] ?? SEAT.silver;
              const isDiamond = tier.id === "diamond";
              const displayPrice = getDisplayPrice(tier);
              const checkoutPriceId = getCheckoutPriceId(tier);
              const tierRoute = routeFor(tier);
              const isCurrent = hasThisTier(tier);
              const isIncluded = !isCurrent && tierRoute.kind === "included";
              const isLoading = loadingPriceId === checkoutPriceId;
              const tierError = errorPriceId === checkoutPriceId ? errorMessage : null;

              return (
                <div
                  key={tier.id}
                  style={{ animationDelay: `${index * 70}ms` }}
                  className={cn(
                    "animate-reveal-in",
                    "relative flex flex-col",
                    index > 0 && "border-t border-border md:border-t-0",
                  )}
                >
                  <div className="relative flex flex-1 flex-col p-6 md:p-7">
                    <div className="flex items-center gap-3">
                      <div className={cn("rounded-lg p-2.5", seat.chip)}>{tier.icon}</div>
                      <div>
                        <h3 className={seat.name}>
                          {tier.name}
                        </h3>
                        {tier.trial && tierRoute.kind === "checkout" && (
                          <span className={cn("text-xs font-medium text-success", trialCopyClass)}>5-day free trial</span>
                        )}
                      </div>
                      {tier.badge && (
                        <span className={cn("ml-auto rounded px-2 py-0.5 data-num text-[10px] font-semibold uppercase tracking-[0.14em]", seat.chip)}>
                          {tier.badge}
                        </span>
                      )}
                    </div>

                    <div className={cn("mt-4 h-px w-full", seat.rule)} />

                    <div className="mt-5 flex items-baseline gap-1.5">
                      <span className="data-num text-4xl font-bold text-foreground">
                        {priceText(tier, displayPrice)}
                      </span>
                      <span className="data-num text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                        /{tier.billingPeriod === "monthly" ? "mo" : "yr"}
                      </span>
                    </div>

                    {tier.billingPeriod === "yearly" &&
                    yearlySavingsById.get(tier.id as BundleTierId) ? (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="inline-flex w-fit items-center rounded bg-accent-value/10 px-2 py-0.5 data-num text-[11px] font-semibold uppercase tracking-wide text-accent-value ring-1 ring-accent-value/25">
                          Save {yearlySavingsById.get(tier.id as BundleTierId)!.pct}%
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatPrice(yearlySavingsById.get(tier.id as BundleTierId)!.perMonth)}/mo billed annually
                        </span>
                      </div>
                    ) : (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {tier.savingsNote && (
                          <span className="inline-flex w-fit items-center rounded bg-accent-value/10 px-2 py-0.5 data-num text-[11px] font-semibold uppercase tracking-wide text-accent-value ring-1 ring-accent-value/25">
                            {tier.savingsNote}
                          </span>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Billed monthly · cancel anytime
                        </p>
                      </div>
                    )}

                    <div className="mt-6 border-t border-border/50 pt-5">
                      {tier.includedSummary && (
                        <p className="mb-3 data-num text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                          {tier.includedSummary}
                        </p>
                      )}
                      <div className="space-y-2.5">
                        {tier.features.map((feature) => (
                          <div key={feature.name} className="flex items-start gap-2.5">
                            <CheckIcon className={cn("mt-0.5 size-4 flex-shrink-0", seat.check)} />
                            <span className="text-sm text-foreground/90">{feature.name}</span>
                          </div>
                        ))}
                      </div>
                      {tier.excludedNote && (
                        <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                          <Minus className="size-3.5 flex-shrink-0" aria-hidden="true" />
                          {tier.excludedNote}
                        </div>
                      )}
                    </div>

                    <div className="mt-auto pt-6" ref={isDiamond ? firstCtaRef : undefined}>
                      {tierError && (
                        <div
                          className="mb-3 rounded border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive"
                          role="alert"
                          aria-live="polite"
                        >
                          {tierError}
                        </div>
                      )}
                      <Button
                        className={cn(
                          "w-full rounded data-num uppercase tracking-wide transition-all duration-200",
                          seat.cta,
                        )}
                        onClick={() => handleSubscribe(tier)}
                        disabled={isLoading || isCurrent || isIncluded}
                      >
                        <span className="flex items-center justify-center gap-2">
                          {isLoading ? (
                            <>
                              <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                              Setting up…
                            </>
                          ) : isCurrent || isIncluded ? (
                            <>
                              {isCurrent ? "Current Plan" : "Included in your plan"}
                              <CheckIcon className="size-4" />
                            </>
                          ) : (
                            <>
                              {getBundleCtaLabel(tier)}
                              <ArrowRightIcon className="size-4" />
                            </>
                          )}
                        </span>
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {showComparison && comparison && (
          <div ref={comparisonRef} className="mt-20">
            <ComparisonBoard bundles={bundles} comparison={comparison} />
          </div>
        )}

        {addOns.length > 0 && (
          <div className="mt-20">
            <div className="terminal-divider mb-6" />
            <div className="mx-auto grid max-w-md grid-cols-1 gap-6">
              {addOns.map((addon) => {
                const isVipDiscord = addon.id === "vip-discord";
                const addonRoute = routeFor(addon);
                const vipIncludedWithDiamond = isVipDiscord && hasLiveSubscriptionSlug("diamond");
                const isCurrent = addonRoute.kind === "included" && !vipIncludedWithDiamond;
                const displayPrice = getDisplayPrice(addon);
                const checkoutPriceId = getCheckoutPriceId(addon);
                const isLoading = loadingPriceId === checkoutPriceId;
                const addonError = errorPriceId === checkoutPriceId ? errorMessage : null;

                return (
                  <div
                    key={addon.id}
                    className="flex flex-col rounded-[var(--radius-panel)] border border-border/60 bg-card/50 p-6"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="text-lg font-semibold text-foreground">{addon.name}</h4>
                          {addon.isNew && (
                            <span className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                              New
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 text-sm text-muted-foreground">{addon.description}</p>
                      </div>
                      <div className="rounded-lg bg-accent-value/10 p-2.5 text-accent-value ring-1 ring-accent-value/25">
                        {addon.icon}
                      </div>
                    </div>

                    <div className="mt-4 flex items-baseline gap-1.5">
                      <span className="data-num text-3xl font-bold text-foreground">
                        {priceText(addon, displayPrice)}
                      </span>
                      <span className="data-num text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                        /{addon.billingPeriod === "monthly" ? "mo" : "yr"}
                      </span>
                    </div>
                    {addon.standaloneNote && addonRoute.kind === "checkout" && (
                      <p className="mt-1 text-xs text-muted-foreground">{addon.standaloneNote}</p>
                    )}

                    <div className="mt-4 space-y-2.5">
                      {addon.features.map((feature) => (
                        <div key={feature.name} className="flex items-start gap-2.5">
                          <CheckIcon className="mt-0.5 size-4 flex-shrink-0 text-accent-value" />
                          <span className="text-sm text-foreground/90">{feature.name}</span>
                        </div>
                      ))}
                    </div>

                    <div className="mt-auto pt-6">
                      {addonError && (
                        <div
                          className="mb-3 rounded border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive"
                          role="alert"
                          aria-live="polite"
                        >
                          {addonError}
                        </div>
                      )}
                      <Button
                        className="w-full rounded border border-border bg-card data-num uppercase tracking-wide text-foreground transition-all duration-200 hover:border-accent-value/40"
                        onClick={() => void handleSubscribe(addon)}
                        disabled={isLoading || isCurrent || vipIncludedWithDiamond}
                      >
                        <span className="flex items-center justify-center gap-2">
                          {isLoading ? (
                            <>
                              <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                              Setting up…
                            </>
                          ) : isCurrent ? (
                            <>
                              Current Plan
                              <CheckIcon className="size-4" />
                            </>
                          ) : vipIncludedWithDiamond ? (
                            <>
                              Included with Diamond
                              <CheckIcon className="size-4" />
                            </>
                          ) : (
                            <>
                              {routeLabel(addonRoute) ?? addon.ctaLabel}
                              <ArrowRightIcon className="size-4" />
                            </>
                          )}
                        </span>
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mx-auto mt-16 max-w-4xl">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {PRICING_TESTIMONIALS.map((testimonial) => (
              <div
                key={testimonial.handle}
                className="flex flex-col gap-3 rounded-xl border border-border/50 bg-card/50 p-4"
              >
                <div className="flex items-start gap-2">
                  <Quote aria-hidden="true" className="mt-0.5 size-4 flex-shrink-0 text-primary/30" />
                  <p className="line-clamp-3 text-xs leading-relaxed text-foreground/80">
                    {testimonial.text}
                  </p>
                </div>
                <div className="mt-auto flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-foreground">{testimonial.name}</p>
                    <p className="text-[10px] text-muted-foreground">{testimonial.handle}</p>
                  </div>
                  <div className="flex items-center gap-1 rounded-full border border-success/20 bg-success/10 px-2 py-0.5">
                    <TrendingUp aria-hidden="true" className="size-3 text-success" />
                    <span className="data-num text-xs font-bold text-success">
                      {testimonial.profit}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {!memberHasPlan && <TrustSignals className={cn("mt-10", trialCopyClass)} />}

        <div className="mt-10 text-center">
          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => phCapture("discord_clicked", { surface, location: "pricing_section" })}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <DiscordMark className="size-4 opacity-60" />
            <span>Questions? Join our Discord community</span>
          </a>
        </div>
      </div>

      {stickyTier && (
          <div
            inert={!showStickyCta}
            className={`fixed inset-x-0 bottom-0 z-40 sm:hidden transform-gpu transition-transform duration-300 motion-reduce:transition-none ${
              showStickyCta ? "translate-y-0" : "translate-y-full pointer-events-none"
            }`}
            style={{ transitionTimingFunction: "var(--ease-spring)" }}
          >
            <div
              className="border-t border-border bg-background/95 px-4 pt-3 shadow-[0_-4px_20px_rgba(0,0,0,0.3)] backdrop-blur-md"
              style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom, 0.75rem))" }}
            >
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground">
                    {stickyTier.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {priceText(stickyTier, stickyTierDisplayPrice ?? stickyTier.price)}/{stickyTier.billingPeriod === "monthly" ? "mo" : "yr"}
                    {stickyTier.trial && stickyTierRoute?.kind === "checkout" ? (
                      <span className={trialCopyClass}> · 5-day free trial</span>
                    ) : null}
                  </p>
                </div>
                <Button
                  aria-label={`Get started with ${stickyTier.name}`}
                  className="flex-shrink-0 rounded bg-accent-value px-5 data-num uppercase tracking-wide text-zinc-950 hover:bg-accent-value-strong"
                  onClick={() => handleSubscribe(stickyTier)}
                  disabled={
                    loadingPriceId === stickyTierCheckoutPriceId ||
                    stickyTierHasSubscription
                  }
                >
                  <span className="flex items-center gap-1.5 text-sm font-semibold">
                    {stickyTierHasSubscription ? (
                      <>
                        Current Plan
                        <CheckIcon className="size-3.5" />
                      </>
                    ) : (
                      <>
                        {getBundleCtaLabel(stickyTier)}
                        <ArrowRightIcon className="size-3.5" />
                      </>
                    )}
                  </span>
                </Button>
              </div>
            </div>
          </div>
      )}

      {chooserLoaded && (
        <PlanChooser
          open={chooserOpen}
          options={chooserOptions}
          note={`${TRIAL_TERMS_SHORT} · Cancel anytime`}
          onSelect={(id) => {
            const tier = chooserTiers.find((candidate) => candidate.id === id);
            if (tier) void handleSubscribe(tier, "plan_chooser");
          }}
          onClose={closeChooser}
        />
      )}

      {upgradeModal && (
        <LazyUpgradeQuoteModal
          open={upgradeModalOpen}
          targetTier={upgradeModal.targetTier}
          targetInterval={upgradeModal.targetInterval}
          source={upgradeModal.source}
          promoCode={couponParam}
          onClose={(result) => {
            setUpgradeModalOpen(false);
            setUpgradeModal(null);
            if (result.type !== "success") return;
            const backToTool = sanitizeRelativeRedirectTarget(redirectTo, "");
            const coversTool = requiredProduct !== null && getBundleEntitlements(result.targetTier).includes(requiredProduct);
            if (backToTool && coversTool) window.location.assign(backToTool);
          }}
        />
      )}
    </section>
  );
}

function ComparisonBoard({
  bundles,
  comparison,
}: {
  bundles: PricingTier[];
  comparison: ComparisonTool[];
}) {
  const order: BundleTierId[] = ["silver", "gold", "diamond"];
  const columns = order
    .map((id) => bundles.find((b) => b.id === id))
    .filter((b): b is PricingTier => Boolean(b));
  const includedCount = (tierId: PricingTierId) =>
    comparison.filter((tool) => tool.includedIn.includes(tierId as BundleTierId)).length;
  const tone = (id: PricingTierId) => {
    if (id === "diamond") {
      return { name: "text-primary", chip: "border-primary/40 bg-primary/15 text-primary" };
    }
    if (id === "gold") {
      return { name: "text-gold", chip: "border-gold/35 bg-gold/15 text-gold" };
    }
    return { name: "text-zinc-200", chip: "border-zinc-300/25 bg-zinc-300/15 text-zinc-200" };
  };

  const gridCols =
    "grid grid-cols-[minmax(0,1.25fr)_repeat(3,minmax(3.25rem,0.75fr))] gap-x-2 sm:grid-cols-[minmax(0,1.5fr)_repeat(3,minmax(4.5rem,0.5fr))] sm:gap-x-3";

  return (
    <div
      data-testid="comparison-board"
      className="overflow-hidden rounded-[var(--radius-panel)] border border-border bg-[radial-gradient(circle_at_top_right,rgba(0,212,255,0.10),transparent_34%),var(--color-surface-raised)]"
    >
      <div className={cn(gridCols, "items-end border-b border-border px-3 py-2.5 sm:px-4")}>
        <span className="data-num text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          Capability
        </span>
        {columns.map((tier) => {
          const tierTone = tone(tier.id);
          const n = includedCount(tier.id);
          return (
            <div key={tier.id} data-comparison-tier={tier.id} className="min-w-0 text-center">
              <div
                className={cn(
                  "font-display font-bold uppercase tracking-tight text-[11px] leading-none sm:text-lg md:text-xl",
                  tierTone.name,
                )}
              >
                {tier.name}
              </div>
              <div className="mt-0.5 data-num text-[9px] uppercase tracking-[0.1em] text-muted-foreground sm:text-[10px]">
                {n} {n === 1 ? "tool" : "tools"}
              </div>
            </div>
          );
        })}
      </div>

      <div>
        {comparison.map((tool, index) => (
          <div
            key={tool.name}
            className={cn(
              "border-b border-border/45 px-3 py-2.5 transition-colors last:border-b-0 hover:bg-white/[0.025] sm:px-4",
              index % 2 === 1 && "bg-white/[0.018]",
              tool.isNew && "bg-primary/[0.05]",
            )}
          >
            <div className={cn(gridCols, "items-center")}>
              <div className="min-w-0 pr-2">
                <div className="flex items-center gap-2">
                  <h4 className="text-[13px] font-medium leading-tight text-foreground sm:text-sm">
                    {tool.name}
                  </h4>
                  {tool.isNew && (
                    <span className="shrink-0 rounded-full border border-primary/30 bg-primary/10 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-primary">
                      New
                    </span>
                  )}
                </div>
                <p className="mt-0.5 hidden truncate text-xs text-muted-foreground sm:block">
                  {tool.description}
                </p>
              </div>
              {columns.map((tier) => {
                const tierTone = tone(tier.id);
                const included = tool.includedIn.includes(tier.id as BundleTierId);
                return (
                  <div key={tier.id} className="flex items-center justify-center">
                    {included ? (
                      <span
                        className={cn(
                          "inline-flex size-5 items-center justify-center rounded-full border sm:size-[1.375rem]",
                          tierTone.chip,
                        )}
                      >
                        <CheckIcon className="size-3" aria-hidden="true" />
                      </span>
                    ) : (
                      <Minus className="size-4 text-muted-foreground/30" aria-hidden="true" />
                    )}
                    <span className="sr-only">
                      {`${tier.name}: ${included ? "included" : "not included"}`}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export { PricingSection };
